import React, { Fragment, useEffect, useState } from "react";
import {
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Row,
  Input,
} from "reactstrap";
import { Btn, H3, Breadcrumbs } from "../../../AbstractElements";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { addUserAccount } from "../../../CommonURL";
import { postDataWithToken } from "../../../Utils/restUtils";
import Transparentspinner from "../../Common/loader/Transparentspinner";

import Select from "react-select";
import { Country } from "country-state-city";
import PhoneInput from "react-phone-input-2";
import { toast } from "react-toastify";
import "react-phone-input-2/lib/style.css";

const schema = yup.object().shape({
  first_name: yup
    .string()
    .required("First name is required")
    .matches(/^[A-Za-z\s]+$/, {
      message: "First name cannot contain numbers",
      excludeEmptyString: true,
    }),

  last_name: yup
    .string()
    .required("Last name is required")
    .matches(/^[A-Za-z\s]+$/, {
      message: "Last name cannot contain numbers",
      excludeEmptyString: true,
    }),
  email: yup.string().email("Invalid email").required("Email is required"),
  country_code: yup.string().required("Country is required"),
  mobile: yup.string().required("Mobile number is required"),
  password: yup
    .string()
    .min(6, "Minimum 6 characters")
    .required("Password is required"),
  confirm_password: yup
    .string()
    .required("Confirm password is required")
    .oneOf([yup.ref("password"), null], "Passwords do not match"),
});

const AddStaffForm = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      country_code: "",
      country: "",
      password: "",
      confirm_password: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const india = Country.getAllCountries().find((c) => c.isoCode === "IN");

    if (india) {
      setSelectedCountry(india);
      setValue("country_code", india.phonecode);
      setValue("country", india.name);
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    const mobileWithoutCode = data.mobile.slice(data.country_code.length);
    try {
      const requestBody = {
        ...data,
        mobile: mobileWithoutCode,
        role_id: 4,
      };

      const res = await postDataWithToken(addUserAccount, requestBody);

     if (res?.data?.status === true) {
  toast.success(res?.data?.message || "Staff added successfully");
  navigate(`${process.env.PUBLIC_URL}/master-records/Staff`);
} else {
  toast.error(
    Array.isArray(res?.data?.message)
      ? res.data.message[0]
      : res?.data?.message || "Failed to add staff"
  );
}
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }

    setLoading(false);
  };

  return (
    <Fragment>
      {loading && <Transparentspinner />}

      <Breadcrumbs
        mainTitle="Add Staff"
        parent="Administration"
        title="Add Staff"
      />

      <Card>
        <CardHeader>
          <H3>Add Staff</H3>
        </CardHeader>

        <CardBody>
          <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Row className="g-3">
              {/* First Name */}
              <Col md="6">
                <Label>
                  First Name <span className="requireStar">*</span>
                </Label>
                <input className="form-control" {...register("first_name")} />
                <span className="text-danger">
                  {errors.first_name?.message}
                </span>
              </Col>

              {/* Last Name */}
              <Col md="6">
                <Label>
                  Last Name <span className="requireStar">*</span>
                </Label>
                <input className="form-control" {...register("last_name")} />
                <span className="text-danger">{errors.last_name?.message}</span>
              </Col>

              {/* Email */}
              <Col md="6">
                <Label>
                  Email <span className="requireStar">*</span>
                </Label>
                <input
                  type="email"
                  className="form-control"
                  {...register("email")}
                />
                <span className="text-danger">{errors.email?.message}</span>
              </Col>
              <Col md="6">
                <Label>
                  Role <span className="requireStar">*</span>{" "}
                </Label>

                <Controller
                  name="role_id"
                  control={control}
                  defaultValue={4}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={[{ label: "Regional admin", value: 4 }]}
                      value={{ label: "Regional admin", value: 4 }}
                      isDisabled
                    />
                  )}
                />
              </Col>

              {/* Country */}
              <Col md="6">
                <Label>
                  Country <span className="requireStar">*</span>
                </Label>
                <Controller
                  name="country_code"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={Country.getAllCountries()}
                      getOptionLabel={(opt) =>
                        `${opt.name} (+${opt.phonecode})`
                      }
                      getOptionValue={(opt) => opt.phonecode}
                      value={selectedCountry}
                      onChange={(item) => {
                        setSelectedCountry(item);
                        field.onChange(item.phonecode);
                        setValue("country", item.name);
                      }}
                    />
                  )}
                />
                <span className="text-danger">
                  {errors.country_code?.message}
                </span>
              </Col>

              {/* Mobile */}
              <Col md="6">
                <Label>
                  Mobile <span className="requireStar">*</span>
                </Label>
                <Controller
                  name="mobile"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      country={"in"}
                      value={field.value}
                      onChange={(value, country) => {
                        field.onChange(value);

                        setValue("country_code", country.dialCode);
                        setValue("country", country.name);
                      }}
                      enableSearch
                      inputStyle={{ width: "100%", height: "38px" }}
                    />
                  )}
                />
                <span className="text-danger">{errors.mobile?.message}</span>
              </Col>

              <Col md="6">
                <Label>
                  Password <span className="requireStar">*</span>
                </Label>

                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <div className="position-relative">
                      <Input
                        {...field}
                        type={showPassword ? "text" : "password"}
                      />

                      <span
                        className="position-absolute end-0 top-50 translate-middle-y me-3 text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "hide" : "show"}
                      </span>
                    </div>
                  )}
                />

                <span className="text-danger">{errors.password?.message}</span>
              </Col>

              {/* Confirm Password */}
              <Col md="6">
                <Label>
                  Confirm Password <span className="requireStar">*</span>
                </Label>

                <Controller
                  name="confirm_password"
                  control={control}
                  render={({ field }) => (
                    <div className="position-relative">
                      <Input
                        {...field}
                        type={showConfirmPassword ? "text" : "password"}
                      />

                      <span
                        className="position-absolute end-0 top-50 translate-middle-y me-3 text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? "hide" : "show"}
                      </span>
                    </div>
                  )}
                />

                <span className="text-danger">
                  {errors.confirm_password?.message}
                </span>
              </Col>
            </Row>

            <Btn
              attrBtn={{ color: "primary", className: "mt-3" }}
              type="submit"
            >
              Add Staff
            </Btn>
          </Form>
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default AddStaffForm;
